
import {
  ControlElement,
  isStringControl,
  JsonFormsRendererRegistryEntry,
  rankWith,
} from "@jsonforms/core";
import { defineComponent, inject, nextTick, toRefs } from "vue";
import { rendererProps, useJsonFormsControl } from "@jsonforms/vue";
import { useVanillaControl } from "@jsonforms/vue-vanilla";
import ControlWrapper from "@/core/json-forms/controls/ControlWrapper.vue";
import {
  getCaretPosition,
  setCaretPosition,
  saveCaretPosition,
  ensureCaretRestored,
  handleBackspaceZWSP,
  copyContainerContent,
  handlePastedHtml,
  isOurSpanHtml,
} from "@/core/json-forms/components/service";
import {
  addOutput as commonAddOutput,
  addMetaoutput as commonAddMetaoutput,
  addUserVariable as commonAddUserVariable,
  addSystemVariable as commonAddSystemVariable,
  addFunction as commonAddFunction,
} from "@/core/json-forms/components/spanInjection";
import RendererConfigurations from "@/core/json-forms/renderer-configurations";
import {
  getTooltipForMetaoutput,
  getTooltipForUdv,
} from "@/flow/application/useTooltipForTags";
import {
  normalizeMetaoutputReference,
  normalizeOutputReference,
  PREFER_ORIG_ID_OUTPUT_REFERENCES_KEY,
} from "@/flow/utils/outputReferenceUtils";
import { EventBus } from "@/eventBus";
import {
  clearActiveFunctionInjectionTarget,
  getFunctionInjectionTarget,
  hasOpenFunctionSelector,
  setActiveFunctionInjectionTarget,
} from "@/core/json-forms/components/functionInjectionTarget";
import FunctionSelector from "../components/FunctionSelector.vue";

export type Items = {
  name: string;
  hasChild: boolean;
  type: string;
  childList: Child[];
};

export type Child = {
  name: string;
  childType: string;
};

const controlRenderer = defineComponent({
  name: RendererConfigurations.StringControlRenderer.name,
  components: {
    FunctionSelector,
    ControlWrapper,
  },
  emits: [
    "update",
    "updatePopUpOpen",
    "handleClickEvent",
    "expressionDrawer",
    "changesHappened",
  ],
  data() {
    return {
      openDialog: false,
      inputMapperDialog: false,
      oldPath: "",
      selectedOutputId: "",
      selectedMetaoutputId: "",
      trackState: false,
      loading: false,
      inputList: [],
      inputDiv: "",
      count: 0,
      currentID: "",
      isContain: false,
      visible: false,
      outputs: toRefs(this.$props).outputList,
      metaoutputs: toRefs(this.$props).metaoutputList,
      systemValues: toRefs(this.$props).systemVariables,
      userDefinedVariablesList: toRefs(this.$props).userDefinedVariables,
      // Temporary switch: keep the click flow intact, but hide expression info.
      expressionInfoEnabled: false,
      showModal: false,
      clickedFunctionName: "",
      selectedVariables: [],
      lastCaretPosition: 0,
      storedRange: null,
      showCopy: false,
      copied: false,
      changeEventTimeout: null,
      injectionTarget: null,
      keyupDebounceTimer: null,
    };
  },
  computed: {
    RendererConfigurations() {
      return RendererConfigurations;
    },
    stepIndex(): number {
      if (this.selectedOutputId !== undefined) {
        const data = this.outputList.find(
          (item: any) => item.ix === this.selectedOutputId
        );
        return data ? data.index : -1;
      }
      return -2;
    },
    stepIndexMetaoutput(): number {
      if (this.selectedMetaoutputId !== undefined) {
        const data = this.metaoutputList.find(
          (item: any) => item.ix === this.selectedMetaoutputId
        );
        return data ? data.index : -1;
      }
      return -2;
    },
  },
  props: {
    stepIdx: Number,
    outputList: {
      required: false,
      type: Array,
      // eslint-disable-next-line vue/require-valid-default-prop
      default: [],
    },
    userDefinedVariables: {
      required: false,
      type: Array,
      // eslint-disable-next-line vue/require-valid-default-prop
      default: [],
    },
    systemVariables: {
      required: false,
      type: Array,
      // eslint-disable-next-line vue/require-valid-default-prop
      default: [],
    },
    expressions: {
      required: false,
      type: Array,
      // eslint-disable-next-line vue/require-valid-default-prop
      default: [],
    },
    metaoutputList: {
      required: false,
      type: Array,
      // eslint-disable-next-line vue/require-valid-default-prop
      default: [],
    },
    showOtherInputs: {
      required: false,
      type: Boolean,
      // eslint-disable-next-line vue/require-valid-default-prop
      default: true,
    },
    ...rendererProps<ControlElement>(),
  },
  setup(props: any) {
    const preferOrigIdOutputReferences = inject(
      PREFER_ORIG_ID_OUTPUT_REFERENCES_KEY,
      true
    );

    return {
      ...useVanillaControl(useJsonFormsControl(props)),
      preferOrigIdOutputReferences,
    };
  },
  methods: {
    getInjectionTargetObject() {
      if (!this.injectionTarget) {
        this.injectionTarget = {
          isAvailable: () => Boolean(this.$refs?.div),
          closeSelector: () => {
            this.visible = false;
          },
          addOutput: (data, first, isNotFirstRender = true) =>
            this.addOutput(data, first, isNotFirstRender),
          addMetaoutput: (data, first, isNotFirstRender = true) =>
            this.addMetaoutput(data, first, isNotFirstRender),
          addUserVariable: (data, first, isNotFirstRender = true) =>
            this.addUserVariable(data, first, isNotFirstRender),
          addSystemVariable: (data, first, isNotFirstRender = true) =>
            this.addSystemVariable(data, first, isNotFirstRender),
          addFunction: (data, first, isNotFirstRender = true) =>
            this.addFunction(data, first, isNotFirstRender),
        };
      }

      return this.injectionTarget;
    },
    setActiveInjectionTarget() {
      setActiveFunctionInjectionTarget(this.getInjectionTargetObject());
    },
    getInjectionTarget(first, isNotFirstRender = true) {
      return getFunctionInjectionTarget(
        this.getInjectionTargetObject(),
        first,
        isNotFirstRender
      );
    },
    onBlur(e: FocusEvent) {
      this.storedRange = saveCaretPosition(e.target as Node);
      if (this.visible) {
        setTimeout(() => {
          if (!this.visible) {
            this.isFocused = false;
          }
        }, 0);
        return;
      }
      this.isFocused = false;
    },
    convertToInputString(isNotFirstRender = true) {
      try {
        const inputArray = [];
        this.$refs.div.childNodes.forEach((child) => {
          if (child instanceof HTMLBRElement) {
            inputArray.push({
              name: Array(1).fill("\n").join(""),
              type: "input",
              function: "",
            });
          } else if (child.nodeType === Node.ELEMENT_NODE) {
            inputArray.push({
              name: child.getAttribute("name"),
              origId: child.getAttribute("origId"),
              type: child.getAttribute("type"),
              function: child.getAttribute("function"),
            });
          } else if (
            child.nodeType === Node.TEXT_NODE &&
            child.textContent !== "" &&
            child.textContent !== "\u200B"
          ) {
            inputArray.push({
              name: child.textContent.replaceAll("\u200B", ""),
              type: "input",
            });
          }
        });
        // An extra line added to make the UI appear is removed here OT-1989
        if (
          inputArray.length > 0 &&
          inputArray[inputArray.length - 1].name === "\n"
        ) {
          inputArray.pop();
        }
        this.selectedVariables = inputArray;
        this.control.data = JSON.stringify(inputArray);
        this.$refs.inputField.value = JSON.stringify(inputArray);
        // Debounce the event dispatch
        if (isNotFirstRender) {
          clearTimeout(this.changeEventTimeout);
          this.changeEventTimeout = setTimeout(() => {
            const evt = new Event("change", {
              bubbles: true,
              cancelable: false,
            });
            const el = document.getElementById(`${this.control.id}-input`);
            if (el) el.dispatchEvent(evt);
          }, 150); // Wait after last keystroke
        }
      } catch (e) {
        console.log(e, "string");
      }
    },
    setCaret(eventCode = null) {
      const el = document.getElementById(`${this.control.id}-div`);
      if (el) {
        // if node is text node this lines set the caret position OT-1989
        const textNode = this.findFirstTextNode(el);
        if (textNode && eventCode !== "Enter") {
          const textLength = textNode.textContent.length;
          const offset = Math.min(8, textLength);
          const range = document.createRange();
          const sel = window.getSelection();

          range.setStart(textNode, offset);
          range.collapse(true);

          sel.removeAllRanges();
          sel.addRange(range);
        } else if (eventCode === "Enter") {
          // if pressed key is Enter this lines set the caret position OT-1989
          const range = document.createRange();
          const sel = window.getSelection();
          const { lastChild } = el;
          if (lastChild.nodeType === Node.TEXT_NODE) {
            range.setStart(lastChild, lastChild.textContent.length);
          } else {
            range.setStart(lastChild, lastChild.childNodes.length);
          }
          range.collapse(true);

          sel.removeAllRanges();
          sel.addRange(range);
        }
      }
    },

    findFirstTextNode(node) {
      // find first text node according to selected element OT-1989
      if (node.nodeType === Node.TEXT_NODE) {
        return node;
      }
      for (let i = 0; i < node.childNodes.length; i++) {
        const textNode = this.findFirstTextNode(node.childNodes[i]);
        if (textNode) {
          return textNode;
        }
      }
      return null;
    },

    insertNewElement() {
      // insert new line and set the caret OT-1989
      const range = document.getSelection().getRangeAt(0);
      range.deleteContents();
      const sel = window.getSelection();
      const br = document.createElement("br");
      range.insertNode(br);
      range.setStartAfter(br);
      range.setEndAfter(br);
      range.collapse(false);
      sel.removeAllRanges();
      sel.addRange(range);
      const newElement = document.createElement("div");
      newElement.textContent = "";
      newElement.contentEditable = "true";

      const parent = range.commonAncestorContainer;
      if (parent.nodeType === Node.TEXT_NODE) {
        parent.parentNode.insertBefore(newElement, parent.nextSibling);
      } else {
        parent.appendChild(newElement);
      }

      range.setStart(newElement, 0);
      range.setEnd(newElement, 0);
    },

    onKeyDown(event) {
      if (event.key === "Enter") {
        // new line was being added but it was not visible on ui, so an extra line was added for this reason. OT-1989
        if (this.$refs.div.childNodes.length === 0) {
          this.addBrElement();
        } else if (this.$refs.div.childNodes.length === 1) {
          const node = this.$refs.div.childNodes[0];
          const isBr =
            node.nodeType === Node.ELEMENT_NODE &&
            node?.tagName?.toLowerCase() === "br";
          if (!isBr) {
            this.addBrElement();
          }
        }
        event.preventDefault();
        this.insertNewElement();
      } else if (event.key === "Backspace") {
        // removes empty line for press backspace
        if (
          this.$refs.div.childNodes.length === 1 &&
          this.$refs.div.childNodes[0] instanceof HTMLBRElement
        ) {
          document
            .getElementById(`${this.control.id}-div`)
            .removeChild(this.$refs.div.childNodes[0]);
        }
        const controlDivId = `${this.control.id}-div`;
        if (handleBackspaceZWSP(this.$refs.div, controlDivId)) {
          event.preventDefault();
        }
      } else if (event.key === "'") {
        // Add zero-width space after each single quotation mark OT-2357
        const selection = window.getSelection();
        const range = selection.getRangeAt(0);
        // Insert the quotation mark and zero-width space OT-2357
        const textNode = document.createTextNode("'\u200B");
        range.insertNode(textNode);

        // Move the caret to after the zero-width space OT-2357
        range.setStartAfter(textNode);
        range.setEndAfter(textNode);
        selection.removeAllRanges();
        selection.addRange(range);

        event.preventDefault();
      }
    },
    onMouseEnter() {
      this.showCopy = true;
      this.copied = false;
    },
    async copyContainerContent() {
      try {
        await copyContainerContent(`${this.control.id}-div`);
        this.copied = true;
        setTimeout(() => {
          this.copied = false;
        }, 2000);
      } catch (e) {
        console.error("Copy failed", e);
      }
    },
    clickEvent(e) {
      this.setActiveInjectionTarget();
      if (e.target.tagName.toLowerCase() === "span") {
        const clickedSpan = e.target;
        const spanType = clickedSpan.getAttribute("type");
        if (
          spanType === "arithmetic-operator" ||
          spanType === "math-function" ||
          spanType === "general-function" ||
          spanType === "text-function" ||
          spanType === "date-function" ||
          spanType === "array-function" ||
          spanType === "encode-decode-function" ||
          spanType === "logic-function" ||
          spanType === "special-values-function"
        ) {
          this.showModal = true;
          this.clickedFunctionName = e.target.innerText.includes("(")
            ? e.target.innerText.substring(0, e.target.innerText.length - 1)
            : e.target.innerText;
          // Only open the side info panel when the temporary switch is re-enabled.
          this.$emit(
            "expressionDrawer",
            this.expressionInfoEnabled && this.showModal
          );
        }
      } else {
        this.showModal = false;
        this.$emit("expressionDrawer", false);
      }
    },
    organizeNodes(event, isNotFirstRender = true) {
      // Dbounce keyup events (user is actively typing)
      if (event && typeof event === "object" && event.type === "keyup") {
        clearTimeout(this.keyupDebounceTimer);
        this.keyupDebounceTimer = setTimeout(() => {
          this.doOrganizeNodes(event, isNotFirstRender);
        }, 150);
        return;
      }

      clearTimeout(this.keyupDebounceTimer);
      this.doOrganizeNodes(event, isNotFirstRender);
    },
    async doOrganizeNodes(event, isNotFirstRender = true) {
      try {
        await nextTick();
        this.lastCaretPosition = getCaretPosition(this.$refs.div);
        this.$refs.div.childNodes.forEach((child, index) => {
          const nextChild = this.$refs.div.childNodes.item(index + 1);
          if (child instanceof HTMLDivElement) {
            this.setCaret(event.code);
            document
              .getElementById(`${this.control.id}-div`)
              .removeChild(child);
          }
          if (nextChild) {
            if (
              nextChild.nodeType === child.nodeType &&
              child.nodeType === Node.TEXT_NODE
            ) {
              const childText = child.textContent.trim();
              const nextText = nextChild.textContent.trim();
              const specialCharsRegex = /^[\u200B"']$/;

              if (
                specialCharsRegex.test(childText) ||
                specialCharsRegex.test(nextText)
              ) {
                return;
              }
              if (
                nextChild.textContent === "\u200B" ||
                child.textContent === "\u200B"
              ) {
                document
                  .getElementById(`${this.control.id}-div`)
                  .removeChild(
                    child.textContent !== "\u200B" ? nextChild : child
                  );
              } else {
                child.textContent += nextChild.textContent;
                child.textContent = child.textContent.replace("\u200B", "");
                document
                  .getElementById(`${this.control.id}-div`)
                  .removeChild(nextChild);
              }
            }
          }
        });
      } catch (e) {
        console.log(e, "organize");
      }
      this.convertToInputString(isNotFirstRender);
    },

    getNextSibling(current?) {
      const position =
        this.$refs.div === document.activeElement
          ? getCaretPosition(this.$refs.div)
          : this.lastCaretPosition;

      let length = 0;
      let childNode;
      let set = false;
      let childIndex = -1;
      this.$refs.div.childNodes.forEach((child, index) => {
        if (child.nodeType === Node.TEXT_NODE) {
          length += child.data.length;
        } else if (child.nodeType === Node.ELEMENT_NODE) {
          length += child.innerText.length;
        }
        if (position <= length && !set && position !== 0) {
          childIndex = index;
          set = true;
        } else if (position === 0) {
          childIndex = -2;
          set = true;
        }
      });
      if (childIndex > -1) {
        return current
          ? {
              item: this.$refs.div.childNodes.item(childIndex),
              index: childIndex,
            }
          : {
              item: this.$refs.div.childNodes.item(childIndex + 1),
              index: childIndex + 1,
            };
      }
      if (childIndex === -2) {
        return {
          item: this.$refs.div.childNodes.item(0),
          index: 0,
        };
      }
      return childNode;
    },
    getInputLengthToCaretPosition(index) {
      let length = 0;
      this.$refs.div.childNodes.forEach((node, idx) => {
        if (node.nodeType === Node.TEXT_NODE && idx < index) {
          length += node.data.length;
        } else if (node.nodeType === Node.ELEMENT_NODE && idx < index) {
          length += node.innerText.length;
        }
      });
      return length;
    },
    addItemToInput(
      span: HTMLSpanElement,
      first,
      isNotFirstRender = true,
      index
    ) {
      ensureCaretRestored(`${this.control.id}-div`, this.storedRange);
      const zeroWidthSpace = document.createTextNode("\u200B");
      const inputContainer = document.getElementById(`${this.control.id}-div`);
      if (
        index !== undefined &&
        index >= 0 &&
        index < inputContainer.childNodes.length
      ) {
        this.insertBefore(span, index);
      } else {
        const current = this.getNextSibling(true);
        const nextNode = this.getNextSibling();
        if (
          current &&
          current.item.nodeType === Node.TEXT_NODE &&
          current.item.data.length > 1 &&
          first !== undefined &&
          !first
        ) {
          const [firstText, second] = this.split(
            current.item.data,
            getCaretPosition(this.$refs.div) -
              this.getInputLengthToCaretPosition(current.index)
          );
          const firstElem = current.item;
          firstElem.data = firstText;
          inputContainer.replaceChild(firstElem, current.item);
          if (nextNode.item) {
            inputContainer.insertBefore(span, nextNode.item);
          } else {
            this.$refs.div.appendChild(span);
          }
          if (nextNode.item) {
            inputContainer.insertBefore(
              document.createTextNode(second),
              nextNode.item
            );
          } else {
            this.$refs.div.appendChild(document.createTextNode(second));
          }
        } else {
          this.addToDocument(
            span,
            zeroWidthSpace,
            nextNode ? nextNode.item : undefined,
            first
          );
        }
      }
      let trailingNode: Text;
      if (span.nextSibling && span.nextSibling.nodeType === Node.TEXT_NODE) {
        trailingNode = span.nextSibling as Text;
        if (
          trailingNode.textContent === "" ||
          trailingNode.textContent === "\u200B"
        ) {
          trailingNode.textContent = "\u200B";
          (trailingNode as any).dataset = { caretAnchor: "true" };
        }
      } else {
        trailingNode = document.createTextNode("\u200B");
        (trailingNode as any).dataset = { caretAnchor: "true" };
        inputContainer.insertBefore(trailingNode, span.nextSibling);
      }
      this.organizeNodes(isNotFirstRender);
      setCaretPosition(span, `${this.control.id}-div`);
      this.lastCaretPosition = getCaretPosition(this.$refs.div);
    },
    addToDocument(span, zero, next, first) {
      if (next && !first && first !== undefined) {
        document
          .getElementById(`${this.control.id}-div`)
          .insertBefore(span, next);
        document
          .getElementById(`${this.control.id}-div`)
          .insertBefore(zero, span);
      } else {
        this.$refs.div.appendChild(span);
        this.$refs.div.appendChild(zero);
      }
    },
    split(text, index) {
      return [text.slice(0, index), text.slice(index)];
    },
    addFunction(item, first, isNotFirstRender = true) {
      const currentTarget = this.getInjectionTargetObject();
      const target = this.getInjectionTarget(first, isNotFirstRender);
      if (target !== currentTarget) {
        if (target.addFunction) {
          target.addFunction(item, first, isNotFirstRender);
        }
        return;
      }

      const spans = commonAddFunction(item, this.count);
      spans.forEach((span) => {
        this.addItemToInput(span, first, isNotFirstRender, item.inputId);
      });
      this.count += spans.length;
      if (item.hasChild) {
        const mainSpan = spans[0];
        const zwsp = mainSpan.nextSibling;
        if (zwsp && zwsp.nodeType === Node.TEXT_NODE) {
          setCaretPosition(zwsp, `${this.control.id}-div`, /* offset */ 1);
          this.lastCaretPosition = getCaretPosition(this.$refs.div);
        }
      }
      this.$emit("changesHappened");
    },
    addOutput(data, first, isNotFirstRender = true) {
      const currentTarget = this.getInjectionTargetObject();
      const target = this.getInjectionTarget(first, isNotFirstRender);
      if (target !== currentTarget) {
        if (target.addOutput) {
          target.addOutput(data, first, isNotFirstRender);
        }
        return;
      }

      if (data.stepIndex > -1) {
        const span = commonAddOutput(
          data,
          this.outputList,
          this.count,
          isNotFirstRender
        );
        this.count++;
        this.addItemToInput(span, first, isNotFirstRender, data.inputId);
      }
    },
    addMetaoutput(data, first, isNotFirstRender = true) {
      const currentTarget = this.getInjectionTargetObject();
      const target = this.getInjectionTarget(first, isNotFirstRender);
      if (target !== currentTarget) {
        if (target.addMetaoutput) {
          target.addMetaoutput(data, first, isNotFirstRender);
        }
        return;
      }

      if (data.stepIndex > -1) {
        const span = commonAddMetaoutput(
          data,
          this.metaoutputList,
          this.count,
          isNotFirstRender
        );
        this.count++;
        this.addItemToInput(span, first, isNotFirstRender, data.inputId);
      }
    },
    addUserVariable(data, first, isNotFirstRender = true) {
      const currentTarget = this.getInjectionTargetObject();
      const target = this.getInjectionTarget(first, isNotFirstRender);
      if (target !== currentTarget) {
        if (target.addUserVariable) {
          target.addUserVariable(data, first, isNotFirstRender);
        }
        return;
      }

      const span: HTMLSpanElement = commonAddUserVariable(
        {
          name: data.name,
          path: data.path,
          stepIndex: 0,
          tooltip: data.tooltip,
        },
        this.userDefinedVariablesList,
        this.count,
        isNotFirstRender
      );
      this.count++;
      this.addItemToInput(span, first, isNotFirstRender, data.inputId);
    },
    addSystemVariable(data, first, isNotFirstRender = true) {
      const currentTarget = this.getInjectionTargetObject();
      const target = this.getInjectionTarget(first, isNotFirstRender);
      if (target !== currentTarget) {
        if (target.addSystemVariable) {
          target.addSystemVariable(data, first, isNotFirstRender);
        }
        return;
      }

      const span = commonAddSystemVariable(
        { name: data.name, path: data.name, stepIndex: 0 },
        this.systemVariables,
        this.count
      );
      this.count++;
      this.addItemToInput(span, first, isNotFirstRender, data.inputId);
    },
    normalizeLineEndings(str, normalized = "\r\n") {
      return str.replace(/\r?\n/g, normalized);
    },
    addText(item) {
      const text = document.createTextNode(
        this.normalizeLineEndings(item.name)
      );
      if (item.inputId !== undefined) {
        this.insertBefore(text, item.inputId);
        this.organizeNodes(false);
      } else {
        this.$refs.div.appendChild(text);
      }
      const newOffset = text.textContent.length;
      setCaretPosition(text, `${this.control.id}-div`, newOffset);
    },
    insertBefore(item, index) {
      const zeroWidthSpace = document.createTextNode("\u200B");
      const inputContainer = document.getElementById(`${this.control.id}-div`);
      const spanElements = inputContainer.querySelectorAll("span");
      let spanArray = Array.from(spanElements);
      const functionIndex = spanArray.findIndex((span) =>
        span.getAttribute("name").includes(this.clickedFunctionName)
      );
      spanArray =
        functionIndex !== -1 ? spanArray.slice(functionIndex) : spanArray;
      const commaArray = spanArray.filter(
        (s) => s.getAttribute("name") === ","
      );
      const parenthesisArray = spanArray.filter(
        (s) => s.getAttribute("name") === ")"
      );
      if (
        index !== undefined &&
        index >= 0 &&
        index < inputContainer.childNodes.length
      ) {
        if (commaArray.length > 0) {
          commaArray.forEach((arr, id) => {
            if (commaArray[0].parentNode === inputContainer && index === 0) {
              if (item.nodeType === Node.TEXT_NODE) {
                inputContainer.replaceChild(
                  item,
                  commaArray[0].previousSibling
                );
              } else {
                inputContainer.insertBefore(item, commaArray[0]);
                inputContainer.insertBefore(zeroWidthSpace, item.nextSibling);
              }
            } else if (
              commaArray[id].parentNode === inputContainer &&
              index === id
            ) {
              if (item.nodeType === Node.TEXT_NODE) {
                inputContainer.replaceChild(
                  item,
                  commaArray[id].previousSibling
                );
                return;
              }
              inputContainer.insertBefore(item, commaArray[id]);
              inputContainer.insertBefore(zeroWidthSpace, item.nextSibling);
            } else if (
              commaArray[id].parentNode === inputContainer &&
              index > id
            ) {
              if (item.nodeType === Node.TEXT_NODE) {
                inputContainer.replaceChild(
                  item,
                  inputContainer.childNodes[
                    inputContainer.childNodes.length -
                      2 * parenthesisArray.length
                  ].previousSibling
                );
              }
              inputContainer.insertBefore(
                item,
                inputContainer.childNodes[
                  inputContainer.childNodes.length - 2 * parenthesisArray.length
                ]
              );
              inputContainer.insertBefore(zeroWidthSpace, item.nextSibling);
            }
          });
        } else {
          if (item.nodeType === Node.TEXT_NODE) {
            inputContainer.replaceChild(
              item,
              parenthesisArray[0].previousSibling
            );
            return;
          }
          const referenceNode = parenthesisArray[0];
          inputContainer.insertBefore(item, referenceNode);
          inputContainer.insertBefore(zeroWidthSpace, item.nextSibling);
        }
      }
    },
    clearPath() {
      const evt = new Event("change", {
        bubbles: true,
        cancelable: false,
      });
      this.oldPath = "";
      this.control.data = "";
      if (this.$refs.inputField) {
        const element = document.getElementById(`${this.control.id}-input`);
        this.$refs.inputField.value = "";
        element.dispatchEvent(evt);
      }
    },
    addClickEvent() {
      try {
        const formElement = document.getElementById(
          `json-form-component${this.$props.stepIdx}`
        );
        if (formElement) {
          formElement.addEventListener(
            "click",
            (event: any) => {
              const divElement = document.getElementById(
                `${this.control.id}-div`
              );
              const popperElement = document.getElementById(
                `${this.control.id}-popper`
              );
              const isContainsDiv = divElement
                ? divElement.contains(event.target)
                : false;
              const isContainsPopper = popperElement
                ? popperElement.contains(event.target)
                : false;

              if (isContainsPopper) {
                this.visible = true;
              } else if (isContainsDiv) {
                this.setActiveInjectionTarget();
                this.visible = this.visible || !hasOpenFunctionSelector();
              } else if (this.visible && formElement.contains(event.target)) {
                this.visible = true;
              } else {
                this.visible = false;
              }

              this.$emit("handleClickEvent", this.visible, this.control.id);
            },
            false
          );
          this.showModal = false;
          this.$emit("expressionDrawer", this.showModal);
        }
      } catch (e) {
        console.log(e, "click");
      }
    },
    addBrElement() {
      const br = document.createElement("br");
      document.getElementById(`${this.control.id}-div`).appendChild(br);
    },
    handlePastedTextWithNewlines(pastedData: string, range: Range) {
      const lines = pastedData.split(/\r?\n/);
      let brCount = lines.length + 1;

      lines.forEach((line) => {
        if (line) {
          const textNode = document.createTextNode(line);
          range.insertNode(textNode);
          range.setStartAfter(textNode);
          range.setEndAfter(textNode);
        }
        if (brCount > 0) {
          const br = document.createElement("br");
          range.insertNode(br);
          range.setStartAfter(br);
          range.setEndAfter(br);
          brCount--;
        }
      });
    },
  },
  mounted() {
    try {
      if (this.control.data) {
        const elements = JSON.parse(this.control.data);
        let newlineCount = false;
        this.selectedVariables = elements;
        elements.forEach((item) => {
          if (item.type === "input") {
            if (item.name === "\n") {
              newlineCount = true;
              this.addBrElement();
            } else {
              this.addText(item);
            }
          } else if (item.type === "output") {
            const normalizedOutput = normalizeOutputReference({
              name: item.name,
              origId: item.origId,
              outputList: this.outputList,
              preferOrigId: this.preferOrigIdOutputReferences,
            });
            const arr = normalizedOutput.name.split("/#");
            const outputStepId =
              typeof normalizedOutput.index === "number"
                ? normalizedOutput.index
                : -1;
            this.selectedOutputId = outputStepId;
            this.addOutput(
              {
                name:
                  outputStepId > -1 && arr.length >= 2
                    ? `/${outputStepId}/#${arr[1]}`
                    : normalizedOutput.name,
                path: outputStepId > -1 && arr.length >= 2 ? `/#${arr[1]}` : "",
                stepIndex: outputStepId,
                stepNumber:
                  outputStepId > -1 ? normalizedOutput.stepNumber : undefined,
                origId: normalizedOutput.origId ?? item.origId,
              },
              true,
              false
            );
          } else if (item.type === "user-defined-variable") {
            const arr = item.name.split("/#");
            const value = arr.length >= 2 ? arr[1] : "";
            let tooltip = getTooltipForUdv(
              value,
              this.userDefinedVariablesList
            );
            tooltip =
              typeof tooltip === "object" ? JSON.stringify(tooltip) : tooltip;
            this.addUserVariable(
              {
                name: arr.length >= 2 ? `/#${arr[1]}` : "",
                path: arr.length >= 2 ? `/#${arr[1]}` : "",
                tooltip,
              },
              true,
              false
            );
          } else if (item.type === "system-variable") {
            const data = {
              name: item.name,
              tooltip: this.systemValues[item.name] || "",
            };
            this.addSystemVariable(data);
          } else if (item.type === "metaoutput") {
            const normalizedMetaoutput = normalizeMetaoutputReference({
              name: item.name,
              origId: item.origId,
              metaoutputList: this.metaoutputList,
              preferOrigId: this.preferOrigIdOutputReferences,
            });
            const arr = normalizedMetaoutput.name.split("/#");
            const metaoutputStepId =
              typeof normalizedMetaoutput.index === "number"
                ? normalizedMetaoutput.index
                : -1;
            const metaPath =
              arr.length >= 2 ? arr[1].split("@#").slice(1).join("@#") : "";
            this.selectedMetaoutputId = metaoutputStepId;
            const tooltip = getTooltipForMetaoutput(
              metaPath,
              metaoutputStepId,
              this.metaoutputList
            );
            this.addMetaoutput(
              {
                name:
                  metaoutputStepId > -1 && metaPath
                    ? `/${metaoutputStepId}/#${metaPath}`
                    : normalizedMetaoutput.name,
                path: metaPath ? `/#${metaPath}` : "",
                stepIndex: metaoutputStepId,
                stepNumber:
                  metaoutputStepId > -1
                    ? normalizedMetaoutput.stepNumber
                    : undefined,
                tooltip,
                origId: normalizedMetaoutput.origId ?? item.origId,
              },
              true,
              false
            );
          } else if (item.name && item.type) {
            const matchedExpression = this.expressions.find(
              (expression) => expression.name === item.name
            );
            const tooltip = matchedExpression?.description;
            const data = { ...item, tooltip };
            this.addFunction(data, true);
          } else if (!item.type) {
            // show \n texts in the inputs
            this.addBrElement();
            if (elements[elements.length - 1].name === "\n") {
              this.addBrElement();
            }
          }
        });
        if (newlineCount) {
          this.addBrElement();
        }
      }
    } catch (e) {
      if (typeof this.control.data === "string") {
        this.addText({
          name: this.control.data,
        });
      }
      this.clearPath();
    }
    this.addClickEvent();
    const undoStack = [];
    const caretStack = [];
    const editorEle = document.getElementById(`${this.control.id}-div`);
    undoStack.push(editorEle.innerHTML);
    caretStack.push(getCaretPosition(editorEle));
    // MutationObserver
    const observer = new MutationObserver(() => {
      if (editorEle.innerHTML !== undoStack[undoStack.length - 1]) {
        undoStack.push(editorEle.innerHTML);
        caretStack.push(getCaretPosition(editorEle));
      }
    });
    observer.observe(editorEle, {
      childList: true,
      subtree: true,
      characterData: true,
    });
    editorEle.addEventListener("paste", (e) => {
      e.stopPropagation();
      e.preventDefault();
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      // eslint-disable-next-line no-nested-ternary
      const clipboardData = e.clipboardData || window.clipboardData;
      const htmlData = clipboardData.getData("text/html");
      const pastedData = clipboardData.getData("Text");
      const selection = document.getSelection();
      const range = selection.getRangeAt(0);
      range.deleteContents();
      EventBus.isAnyChange = true;
      if (isOurSpanHtml(htmlData)) {
        handlePastedHtml(
          htmlData,
          editorEle,
          range,
          this.outputList,
          this.metaoutputList,
          {
            preferOrigId: this.preferOrigIdOutputReferences,
          }
        );
        return;
      }
      if (pastedData.includes("\n")) {
        this.handlePastedTextWithNewlines(pastedData, range);
      } else {
        const newTextNode = document.createTextNode(pastedData);
        const currentNode = range.startContainer;
        if (currentNode.nodeType === Node.TEXT_NODE) {
          const caretPosition = range.endOffset;
          const { textContent } = currentNode;
          const updatedTextContent =
            textContent.substring(0, caretPosition) +
            pastedData +
            textContent.substring(caretPosition);
          currentNode.textContent = updatedTextContent;
          undoStack.push(editorEle.innerHTML);
          caretStack.push(caretPosition + pastedData.length);
          setCaretPosition(
            currentNode,
            `${this.control.id}-div`,
            caretPosition + pastedData.length
          );
        } else {
          range.insertNode(newTextNode);
          range.selectNodeContents(newTextNode);
          range.collapse(false);
          selection.removeAllRanges();
          selection.addRange(range);
        }
      }
    });
    editorEle.addEventListener("keydown", (event) => {
      if (event.ctrlKey && event.key === "z") {
        event.preventDefault();
        if (undoStack.length > 1) {
          undoStack.pop();
          editorEle.innerHTML = undoStack[undoStack.length - 1];
          if (editorEle.innerHTML.trim() === "") {
            const emptyTextNode = document.createTextNode("\u200B"); // Zero-width space
            editorEle.appendChild(emptyTextNode);
            setCaretPosition(emptyTextNode, `${editorEle.id}`);
            return;
          }
          let lastNode = editorEle.lastChild;
          while (
            lastNode &&
            lastNode.nodeType === Node.ELEMENT_NODE &&
            lastNode.nodeName !== "BR"
          ) {
            lastNode = lastNode.previousSibling;
          }
          if (lastNode && lastNode.nodeName === "BR") {
            setCaretPosition(lastNode, `${editorEle.id}`);
          } else {
            let textNode = editorEle.lastChild;
            while (textNode && textNode.nodeType !== Node.TEXT_NODE) {
              textNode = textNode.previousSibling;
            }
            if (!textNode) {
              textNode = document.createTextNode("\u200B");
              editorEle.appendChild(textNode);
            }
            setCaretPosition(textNode, `${editorEle.id}`);
          }
        }
      }
    });
  },
  beforeUnmount() {
    if (this.injectionTarget) {
      clearActiveFunctionInjectionTarget(this.injectionTarget);
    }
    clearTimeout(this.changeEventTimeout);
    clearTimeout(this.keyupDebounceTimer);
  },
});

export default controlRenderer;

export const entry: JsonFormsRendererRegistryEntry = {
  renderer: controlRenderer,
  tester: rankWith(2, isStringControl),
};
